import { inject, Injectable } from '@angular/core';
import { DocumentReferenceData, ExtendedMedicalData } from '@pxl/models/common';
import { CommonService, DocumentReferenceService, PatientService } from '@pxl/models/contracts';
import { GenderModel } from '@pxl/shared/generated';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable()
export class DefaultDocumentReferenceService extends DocumentReferenceService {
    private readonly patientService = inject(PatientService);
    private readonly commonService = inject(CommonService);

    public fetchDocumentReferenceData(patientId: string): Observable<DocumentReferenceData> {
        return combineLatest([
            this.patientService.getDocumentToFillDataForPatient(patientId),
            this.commonService.onCommonReferenceData(),
            this.commonService.onBloodGroups(),
            this.commonService.onIntolerancesOrAllergies(),
            this.commonService.onPreExistingConditions(),
            this.commonService.onGenders(),
        ]).pipe(
            take(1),
            map(
                ([
                    documentToFillDataForPatient,
                    commonReferenceData,
                    bloodGroups,
                    intolerancesOrAllergies,
                    preExistingConditions,
                    genders,
                ]) => {
                    const data: DocumentReferenceData = {
                        patient: documentToFillDataForPatient.patientDataModel,
                        patientMedicalData: documentToFillDataForPatient.patientMedicalDataModel,
                        nonRabiesVaccinations: documentToFillDataForPatient.nonRabiesVaccinations,
                        rabiesVaccinations: documentToFillDataForPatient.rabiesVaccinations,
                        treatments: documentToFillDataForPatient.treatments,
                        medicalData: documentToFillDataForPatient.patientMedicalDataModel,
                        medications: documentToFillDataForPatient.medications,
                        activeMedications: documentToFillDataForPatient.activeMedications,
                        referenceDataFavorites: documentToFillDataForPatient.referenceDataFavorites,
                        extendedMedicalData: new ExtendedMedicalData(
                            patientId,
                            documentToFillDataForPatient.patientExtendedMedicalDataModel.nonDomesticTravels,
                            documentToFillDataForPatient.patientExtendedMedicalDataModel.notes,
                        ),
                        weightHistory: documentToFillDataForPatient.weightHistory,
                        species: commonReferenceData.species,
                        genders: this.getGenderItems(genders, documentToFillDataForPatient.patientDataModel.speciesId),
                        preExistingConditions,
                        bloodGroups,
                        intolerancesOrAllergies,
                    };
                    return data;
                },
            ),
        );
    }

    private getGenderItems(genders: GenderModel[], speciesId: string | null = null): GenderModel[] {
        const speciesSpecific = genders.filter((g) => g.speciesId === speciesId);
        if (speciesSpecific.length > 0) return speciesSpecific;
        if (speciesId === null) return [];
        return this.getGenderItems(genders, null);
    }
}
